/**
 * DeployStatusIndicator — DigitalOcean status light in the header.
 * Always visible when DO env is configured; pulses blue while deploying
 * so you know not to save mid-deploy.
 * Polls GET /api/deploy-status (auth). Stops polling after auth failure
 * so expired sessions do not spam 401s in the console.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import { AUTH_EXPIRED_EVENT } from '@/utils/apiAuth';

const API = API_BASE_URL;
const POLL_MS_IDLE = 20000;
const POLL_MS_DEPLOYING = 5000;

export function DeployStatusIndicator() {
  const [state, setState] = useState({
    deploying: false,
    configured: false,
    phase: null,
    message: '',
    status: 'unknown',
  });
  const [justReady, setJustReady] = useState(false);
  const wasDeploying = useRef(false);
  const timerRef = useRef(null);
  const stopPollingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    stopPollingRef.current = true;
    clearTimer();
    setState((prev) => ({ ...prev, configured: false, deploying: false, status: 'auth' }));
  }, [clearTimer]);

  const poll = useCallback(async () => {
    if (stopPollingRef.current) return 'stop';
    try {
      const res = await fetch(`${API}/api/deploy-status`, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        // Not signed in / session expired — hide and stop (share pages / logged-out).
        stopPolling();
        return 'stop';
      }
      if (!res.ok) {
        setState((prev) => ({
          ...prev,
          configured: true,
          deploying: false,
          status: 'unavailable',
          message: `Deploy status HTTP ${res.status}`,
        }));
        return 'ok';
      }
      const data = await res.json();
      const status = data?.status || 'unknown';
      if (status === 'unconfigured') {
        setState({
          deploying: false,
          configured: false,
          phase: null,
          message: data?.message || '',
          status,
        });
        return 'ok';
      }
      const deploying = Boolean(data?.deploying) || status === 'deploying';
      if (wasDeploying.current && !deploying && status === 'ready') {
        setJustReady(true);
        setTimeout(() => setJustReady(false), 12000);
      }
      wasDeploying.current = deploying;
      setState({
        deploying,
        configured: true,
        phase: data?.phase || null,
        message: data?.message || '',
        status,
      });
      return 'ok';
    } catch {
      // Keep last known state on transient network errors during deploy cutover.
      return 'ok';
    }
  }, [stopPolling]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || stopPollingRef.current) return;
      const result = await poll();
      if (cancelled || stopPollingRef.current || result === 'stop') return;
      const delay = wasDeploying.current ? POLL_MS_DEPLOYING : POLL_MS_IDLE;
      timerRef.current = setTimeout(tick, delay);
    };
    tick();

    const onAuthExpired = () => {
      stopPolling();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);

    return () => {
      cancelled = true;
      clearTimer();
      window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    };
  }, [poll, stopPolling, clearTimer]);

  if (!state.configured) return null;

  if (state.deploying) {
    return (
      <button
        type="button"
        className="deploy-pulse-btn flex items-center gap-1 px-1.5 py-1.5 min-h-[28px] rounded"
        title={state.message || `Deployment in progress${state.phase ? ` (${state.phase})` : ''} — wait before saving`}
        aria-label="DigitalOcean deployment in progress — wait before saving"
        data-testid="deploy-status-pulse"
      >
        <span className="deploy-pulse-dot" aria-hidden="true" />
        <span className="hidden sm:inline text-[8px] font-bold uppercase tracking-wide text-sky-700">
          Deploy
        </span>
      </button>
    );
  }

  if (justReady) {
    return (
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="flex items-center gap-1 px-1.5 py-1.5 min-h-[28px] rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
        title="Deploy finished — click to refresh"
        aria-label="Deploy finished — refresh"
        data-testid="deploy-status-ready"
      >
        <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true" />
        <span className="hidden sm:inline text-[8px] font-bold uppercase tracking-wide">Ready</span>
      </button>
    );
  }

  if (state.status === 'error') {
    return (
      <span
        className="flex items-center gap-1 px-1.5 py-1.5 min-h-[28px] text-red-600"
        title={state.message || 'Deploy failed'}
        data-testid="deploy-status-error"
      >
        <span className="w-2 h-2 rounded-full bg-red-500" aria-hidden="true" />
        <span className="hidden sm:inline text-[8px] font-bold uppercase">Fail</span>
      </span>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <span
        className="flex items-center gap-1 px-1.5 py-1.5 min-h-[28px] text-amber-600"
        title={state.message || 'DigitalOcean status unavailable — check DIGITALOCEAN_APP_ID / token'}
        data-testid="deploy-status-unavailable"
      >
        <span className="w-2 h-2 rounded-full bg-amber-400" aria-hidden="true" />
        <span className="hidden sm:inline text-[8px] font-bold uppercase tracking-wide">DO</span>
      </span>
    );
  }

  // Idle / ready — quiet always-on light between deploys.
  return (
    <span
      className="flex items-center gap-1 px-1.5 py-1.5 min-h-[28px] text-sky-600"
      title={state.message || 'DigitalOcean: deployment ready'}
      aria-label="DigitalOcean deployment ready"
      data-testid="deploy-status-idle"
    >
      <span className="w-2 h-2 rounded-full bg-sky-500" aria-hidden="true" />
      <span className="hidden sm:inline text-[8px] font-bold uppercase tracking-wide text-sky-700/80">
        DO
      </span>
    </span>
  );
}
